import { useState } from 'react'
import { X, Check, AlertCircle, LogOut } from 'lucide-react'
import axios from 'axios'
import useAuthStore from '../store/auth-store'
import { useNavigate } from 'react-router-dom'
import { handleSessionExpired } from '../lib/sessionExpiry'

export function ChangePasswordModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [messageType, setMessageType] = useState('success')
  const [succeeded, setSucceeded] = useState(false)
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })

  const logout = useAuthStore(s => s.logout)
  const navigate = useNavigate()

  const reset = () => {
    setFormData({ current_password: '', new_password: '', confirm_password: '' })
    setMessage(null)
    setSucceeded(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleChangePassword = async () => {
    // ── client-side validation ──────────────────────────────────────────────
    if (!formData.current_password) {
      setMessageType('error'); setMessage('Please enter your current password'); return
    }
    if (!formData.new_password) {
      setMessageType('error'); setMessage('Please enter a new password'); return
    }
    if (!formData.confirm_password) {
      setMessageType('error'); setMessage('Please confirm your new password'); return
    }
    if (formData.new_password !== formData.confirm_password) {
      setMessageType('error'); setMessage('New passwords do not match'); return
    }
    if (formData.new_password.length < 8) {
      setMessageType('error'); setMessage('Password must be at least 8 characters'); return
    }
    if (formData.current_password === formData.new_password) {
      setMessageType('error'); setMessage('New password must be different from your current password'); return
    }

    setLoading(true)
    setMessage(null)

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
      const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
      const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
      const token = localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY)

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.')
      }

      const REFRESH_KEY = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refresh_token'
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/account/change-password/`,
        { current_password: formData.current_password, new_password: formData.new_password },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      )

      // Replace the old JWT with fresh tokens so the pre-change token is
      // immediately invalidated from the client side.
      const resData = response.data
      if (resData.access_token) {
        localStorage.setItem(JWT_STORAGE_KEY, resData.access_token)
        sessionStorage.setItem(JWT_STORAGE_KEY, resData.access_token)
      }
      if (resData.refresh_token) {
        localStorage.setItem(REFRESH_KEY, resData.refresh_token)
        sessionStorage.setItem(REFRESH_KEY, resData.refresh_token)
      }

      // Success — clear form and show stay/logout choice
      setFormData({ current_password: '', new_password: '', confirm_password: '' })
      setSucceeded(true)
    } catch (error) {
      setMessageType('error')
      const status = error.response?.status
      if (status === 401) {
        // Token expired — redirect via the shared handler so the
        // "Your session has expired" flash banner appears on the login page.
        handleSessionExpired()
        return
      }
      const data = error.response?.data
      const msg = data?.error || data?.detail || data?.message || error.message || 'Failed to change password. Please try again.'
      setMessage(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleStayLoggedIn = () => {
    handleClose()
  }

  const handleLogOut = () => {
    logout()
    navigate('/login')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-start justify-between p-8 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900">Change Password</h2>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition ml-4" title="Close">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Success state — stay or log out */}
          {succeeded ? (
            <div className="space-y-5">
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Password changed successfully!</p>
                  <p className="mt-1 text-green-700">For security, we recommend logging out and signing back in with your new password.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleStayLoggedIn}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition"
                >
                  Stay Logged In
                </button>
                <button
                  onClick={handleLogOut}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-semibold flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Log Out Now
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Error message */}
              {message && (
                <div className="mb-6 p-4 rounded-lg text-sm flex items-center gap-2 bg-red-50 border border-red-200 text-red-800">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {message}
                </div>
              )}

              <div className="space-y-4">
                <input
                  type="password"
                  name="current_password"
                  value={formData.current_password}
                  onChange={handleInputChange}
                  placeholder="Current Password"
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                />
                <input
                  type="password"
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleInputChange}
                  placeholder="New Password"
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                />
                <input
                  type="password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  placeholder="Confirm New Password"
                  disabled={loading}
                  onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer — hidden after success (buttons are inline above) */}
        {!succeeded && (
          <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition disabled:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleChangePassword}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:bg-blue-400 font-semibold"
            >
              {loading ? 'Changing...' : 'CHANGE PASSWORD'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
